import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle } from "lucide-react";
import {
	registerStep1Mutation,
	completeRegistrationMutation,
	createSetupSessionMutation,
	getSignupLinkDataOptions,
} from "@/lib/client/@tanstack/react-query.gen";
import { toast } from "sonner";
import type { Student } from "@/lib/client/types.gen";
import { z } from "zod";

import { Step1LegalGuardian } from "@/components/inscription/Step1LegalGuardian";
import { Step2ChildrenList } from "@/components/inscription/Step2ChildrenList";
import { Step2_1ChildInfo } from "@/components/inscription/Step2_1ChildInfo";
import { Step2_2SubjectsLevels } from "@/components/inscription/Step2_2SubjectsLevels";
import { Step2_3TimeSlots } from "@/components/inscription/Step2_3TimeSlots";
import { Step2_4PapPps } from "@/components/inscription/Step2_4PapPps";
import { Step3ValidationPayment } from "@/components/inscription/Step3ValidationPayment";
import { PublicHeader } from "@/components/PublicHeader";
import { PublicFooter } from "@/components/PublicFooter";
import { InscriptionSteps } from "@/components/InscriptionSteps";
import { ExperienceSection } from "@/components/ExperienceSection";
import { CoursSection } from "@/components/CoursSection";

const inscriptionSearchSchema = z.object({
	token: z.string().optional(),
});

export const Route = createFileRoute("/inscription")({
	component: PublicRegistrationPage,
	validateSearch: inscriptionSearchSchema,
});

export interface StudentData {
	/** Identifiant d'un enfant déjà connu (réinscription d'un ancien élève) */
	id?: string;
	firstName: string;
	lastName: string;
	school: "SAINT_ANDRE" | "AUTRE";
	schoolOther?: string;
	level:
		| "CM1"
		| "CM2"
		| "SIXIEME"
		| "CINQUIEME"
		| "QUATRIEME"
		| "TROISIEME"
		| "SECONDE"
		| "PREMIERE"
		| "TERMINALE";
	className: string;
	subjects: Student["subjects"];
	timeSlots: Student["timeSlots"];
	hasPap: boolean;
	hasPps: boolean;
}

export interface FormData {
	// Legal guardian
	firstName: string;
	lastName: string;
	email: string;
	phone: string;
	address: string;
	addressLatitude?: number;
	addressLongitude?: number;

	// Children
	students: StudentData[];

	// Billing
	paymentMethod: "CARD" | "SEPA" | "BANK_TRANSFER";
	paymentDayOfMonth: number;

	// Comments
	comments: string;
}

type Step =
	| "1-guardian"
	| "2-children-list"
	| "2.1-child-info"
	| "2.2-subjects"
	| "2.3-timeslots"
	| "2.4-pap-pps"
	| "3-validation";

function PublicRegistrationPage() {
	const { token } = Route.useSearch();
	const [currentStep, setCurrentStep] = useState<Step>("1-guardian");
	const [isSubmitted, setIsSubmitted] = useState(false);
	const [currentChildIndex, setCurrentChildIndex] = useState<number | null>(
		null,
	);
	const [isEditingExistingChild, setIsEditingExistingChild] = useState(false);
	const [tempStudent, setTempStudent] = useState<StudentData | null>(null);
	const [parentId, setParentId] = useState<string | null>(null);
	// Enfants déjà rattachés au parent retrouvé à l'étape 1 (réinscription)
	const [existingStudents, setExistingStudents] = useState<Student[]>([]);
	const [prefilled, setPrefilled] = useState(false);

	const [formData, setFormData] = useState<FormData>({
		firstName: "",
		lastName: "",
		email: "",
		phone: "",
		address: "",
		students: [],
		paymentMethod: "CARD",
		paymentDayOfMonth: 5,
		comments: "",
	});

	const { data: signupLinkData } = useQuery({
		...getSignupLinkDataOptions({
			path: { token: token || "" },
		}),
		enabled: !!token,
	});

	useEffect(() => {
		if (signupLinkData && !prefilled) {
			setPrefilled(true);
			setFormData((prev) => ({
				...prev,
				phone: signupLinkData.parentPhone || prev.phone,
				students: [
					{
						firstName: signupLinkData.studentFirstName || "",
						lastName: signupLinkData.studentLastName || "",
						school: "SAINT_ANDRE",
						schoolOther: "",
						level: "CM1",
						className: signupLinkData.className || "",
						subjects: [],
						timeSlots: [],
						hasPap: false,
						hasPps: false,
					},
				],
			}));
		}
	}, [signupLinkData, prefilled]);

	const { mutate: registerStep1, isPending: isPendingStep1 } = useMutation({
		...registerStep1Mutation(),
		onSuccess: (data) => {
			setParentId(data.id!);
			const knownStudents = data.students ?? [];
			setExistingStudents(knownStudents);
			if (knownStudents.length > 0) {
				toast.success(
					"Nous avons retrouvé votre compte : vous pouvez réinscrire vos enfants déjà connus.",
				);
			} else {
				toast.success("Informations enregistrées !");
			}
			goToNextStep();
		},
		onError: () => {
			toast.error("Une erreur est survenue lors de l'enregistrement");
		},
	});

	const { mutate: createStripeSession, isPending: isCreatingSession } =
		useMutation({
			...createSetupSessionMutation(),
			onSuccess: (sessionUrl) => {
				console.log("Session URL reçue:", sessionUrl);
				console.log("Type:", typeof sessionUrl);
				if (!sessionUrl || typeof sessionUrl !== "string") {
					console.error("URL invalide", sessionUrl);
					toast.error("URL de session invalide");
					window.location.href = "/inscription-stripe-unknown";
					return;
				}
				console.log("Redirection vers:", sessionUrl);
				window.location.href = sessionUrl;
			},
			onError: (error) => {
				console.error(
					"Erreur lors de la création de la session Stripe:",
					error,
				);
				toast.error("Erreur lors de la création de la session Stripe");
				window.location.href = "/inscription-stripe-unknown";
			},
		});

	const { mutate: completeRegistration, isPending } = useMutation({
		...completeRegistrationMutation(),
		onSuccess: () => {
			toast.success("Inscription enregistrée avec succès !");
			if (
				formData.paymentMethod === "CARD" ||
				formData.paymentMethod === "SEPA"
			) {
				if (!parentId) {
					console.error("Parent ID manquant");
					toast.error("Erreur: ID du parent manquant");
					window.location.href = "/inscription-stripe-unknown";
					return;
				}
				createStripeSession({
					path: { parentId },
				});
			} else {
				setIsSubmitted(true);
			}
		},
		onError: () => {
			toast.error("Une erreur est survenue lors de l'inscription");
		},
	});

	const handleStep1Submit = () => {
		registerStep1({
			body: {
				firstName: formData.firstName,
				lastName: formData.lastName,
				email: formData.email,
				phone: formData.phone,
				address: formData.address,
				addressLatitude: formData.addressLatitude,
				addressLongitude: formData.addressLongitude,
			},
		});
	};

	const handleSubmit = () => {
		if (!parentId) {
			toast.error("Erreur: ID du parent manquant");
			return;
		}

		completeRegistration({
			path: { id: parentId },
			body: {
				paymentMethod: formData.paymentMethod,
				comments: formData.comments,
				students: formData.students.map((s) => ({
					id: s.id,
					firstName: s.firstName,
					lastName: s.lastName,
					school: s.school,
					schoolOther: s.schoolOther,
					level: s.level,
					className: s.className,
					subjects: s.subjects,
					timeSlots: s.timeSlots,
					hasPap: s.hasPap,
					hasPps: s.hasPps,
					parentId,
				})),
			},
		});
	};

	const updateFormData = (updates: Partial<FormData>) => {
		setFormData((prev) => ({ ...prev, ...updates }));
	};

	const updateStudent = (updates: Partial<StudentData>) => {
		setTempStudent((prev) => (prev ? { ...prev, ...updates } : null));
	};

	const addStudent = () => {
		// Créer un nouvel enfant temporaire
		const newStudent: StudentData = {
			firstName: "",
			lastName: "",
			school: "SAINT_ANDRE",
			schoolOther: "",
			level: "CM1",
			className: "",
			subjects: [],
			timeSlots: [],
			hasPap: false,
			hasPps: false,
		};
		setTempStudent(newStudent);
		setCurrentChildIndex(null);
		setIsEditingExistingChild(false);
		setCurrentStep("2.1-child-info");
	};

	const reenrollExistingStudent = (existing: Student) => {
		// Pré-remplir le parcours "enfant" avec les informations déjà connues :
		// le parent vérifie et met à jour (classe, niveau, matières...) puis valide.
		const student: StudentData = {
			id: existing.id,
			firstName: existing.firstName ?? "",
			lastName: existing.lastName ?? "",
			school: existing.school ?? "SAINT_ANDRE",
			schoolOther: existing.schoolOther ?? "",
			level: existing.level ?? "CM1",
			className: existing.className ?? "",
			subjects: existing.subjects ?? [],
			timeSlots: existing.timeSlots ?? [],
			hasPap: existing.hasPap ?? false,
			hasPps: existing.hasPps ?? false,
		};
		setTempStudent(student);
		setCurrentChildIndex(null);
		setIsEditingExistingChild(false);
		setCurrentStep("2.1-child-info");
	};

	const editStudent = (index: number) => {
		setCurrentChildIndex(index);
		setTempStudent(formData.students[index]);
		setIsEditingExistingChild(true);
		setCurrentStep("2.1-child-info");
	};

	const saveCurrentStudent = () => {
		if (!tempStudent) return;

		if (isEditingExistingChild && currentChildIndex !== null) {
			// Mise à jour d'un enfant existant
			setFormData((prev) => ({
				...prev,
				students: prev.students.map((s, i) =>
					i === currentChildIndex ? tempStudent : s,
				),
			}));
		} else {
			// Ajout d'un nouvel enfant
			setFormData((prev) => ({
				...prev,
				students: [...prev.students, tempStudent],
			}));
		}

		// Reset
		setTempStudent(null);
		setCurrentChildIndex(null);
		setIsEditingExistingChild(false);
	};

	const cancelStudentEdit = () => {
		// Annuler l'édition sans sauvegarder
		setTempStudent(null);
		setCurrentChildIndex(null);
		setIsEditingExistingChild(false);
		setCurrentStep("2-children-list");
	};

	const deleteStudent = (index: number) => {
		setFormData((prev) => ({
			...prev,
			students: prev.students.filter((_, i) => i !== index),
		}));
	};

	const goToNextStep = () => {
		// Si on termine l'édition d'un enfant (étape 2.4-pap-pps), sauvegarder
		if (currentStep === "2.4-pap-pps") {
			saveCurrentStudent();
		}

		const stepFlow: Record<Step, Step> = {
			"1-guardian": "2-children-list",
			"2-children-list": "3-validation",
			"2.1-child-info": "2.2-subjects",
			"2.2-subjects": "2.3-timeslots",
			"2.3-timeslots": "2.4-pap-pps",
			"2.4-pap-pps": "2-children-list",
			"3-validation": "3-validation", // End
		};
		setCurrentStep(stepFlow[currentStep]);
	};

	const goToPreviousStep = () => {
		// Si on revient depuis 2.1-child-info, annuler l'édition
		if (currentStep === "2.1-child-info") {
			cancelStudentEdit();
			return;
		}

		const previousSteps: Record<Step, Step> = {
			"1-guardian": "1-guardian",
			"2-children-list": "1-guardian",
			"2.1-child-info": "2-children-list",
			"2.2-subjects": "2.1-child-info",
			"2.3-timeslots": "2.2-subjects",
			"2.4-pap-pps": "2.3-timeslots",
			"3-validation": "2-children-list",
		};
		setCurrentStep(previousSteps[currentStep]);
	};

	if (isSubmitted) {
		return (
			<div className="min-h-screen flex flex-col bg-gradient-to-br from-blue-50 to-indigo-100">
				<PublicHeader />
				<div className="flex-1 flex items-center justify-center py-12 px-4">
					<Card className="w-full max-w-md">
						<CardHeader className="text-center">
							<div className="mx-auto mb-4 h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
								<CheckCircle className="h-6 w-6 text-green-600" />
							</div>
							<CardTitle className="text-2xl text-green-800">
								Inscription réussie !
							</CardTitle>
							<CardDescription className="text-center text-gray-600">
								Merci pour votre inscription. Notre équipe vous contactera dans
								les plus brefs délais pour finaliser l'inscription de votre/vos
								enfant(s).
							</CardDescription>
						</CardHeader>
						<CardContent>
							<div className="text-center space-y-4">
								<p className="text-sm text-gray-600">
									Un email de confirmation vous a été envoyé à l'adresse
									fournie.
								</p>
								<Button
									onClick={() => window.location.reload()}
									variant="outline"
									className="w-full"
								>
									Nouvelle inscription
								</Button>
							</div>
						</CardContent>
					</Card>
				</div>
				<PublicFooter />
			</div>
		);
	}

	const getStepTitle = (): string => {
		switch (currentStep) {
			case "1-guardian":
				return "Responsable légal";
			case "2-children-list":
				return "Liste des enfants";
			case "2.1-child-info":
				return `Enfant ${currentChildIndex !== null ? currentChildIndex + 1 : ""} - Informations`;
			case "2.2-subjects":
				return `Enfant ${currentChildIndex !== null ? currentChildIndex + 1 : ""} - Matières et Niveau`;
			case "2.3-timeslots":
				return `Enfant ${currentChildIndex !== null ? currentChildIndex + 1 : ""} - Créneaux`;
			case "2.4-pap-pps":
				return `Enfant ${currentChildIndex !== null ? currentChildIndex + 1 : ""} - PAP/PPS`;
			case "3-validation":
				return "Validation et paiement";
			default:
				return "";
		}
	};

	const getCurrentStudent = (): StudentData | undefined => {
		return tempStudent || undefined;
	};

	return (
		<div className="min-h-screen flex flex-col bg-white">
			<PublicHeader />

			<div
				id="inscription-form"
				className="bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4"
			>
				<div className="max-w-4xl mx-auto">
					<div className="text-center mb-8">
						<h2 className="text-4xl font-bold text-gray-900 mb-4">
							Formulaire d'inscription{" "}
							<span className="text-[#F6993F]">Révacours</span>
						</h2>
						<p className="text-xl text-gray-600 max-w-2xl mx-auto">
							Remplissez ce formulaire étape par étape pour inscrire votre
							enfant.
						</p>
					</div>

					<Card className="w-full shadow-lg">
						<CardHeader>
							<CardTitle>{getStepTitle()}</CardTitle>
							<CardDescription>
								Étape {currentStep.startsWith("2.") ? "2" : currentStep.charAt(0)}{" "}
								sur 3
							</CardDescription>
						</CardHeader>
						<CardContent>
							<div className="space-y-6">
								{currentStep === "1-guardian" && (
									<Step1LegalGuardian
										formData={formData}
										updateFormData={updateFormData}
										onNext={handleStep1Submit}
										isPending={isPendingStep1}
									/>
								)}

								{currentStep === "2-children-list" && (
									<Step2ChildrenList
										students={formData.students}
										existingStudents={existingStudents}
										onReenrollExisting={reenrollExistingStudent}
										onAddChild={addStudent}
										onEditChild={editStudent}
										onDeleteChild={deleteStudent}
										onNext={goToNextStep}
										onPrevious={goToPreviousStep}
									/>
								)}

								{currentStep === "2.1-child-info" && tempStudent && (
									<Step2_1ChildInfo
										student={getCurrentStudent()!}
										onUpdate={updateStudent}
										onNext={goToNextStep}
										onPrevious={goToPreviousStep}
									/>
								)}

								{currentStep === "2.2-subjects" && tempStudent && (
									<Step2_2SubjectsLevels
										student={getCurrentStudent()!}
										onUpdate={updateStudent}
										onNext={goToNextStep}
										onPrevious={goToPreviousStep}
									/>
								)}

								{currentStep === "2.3-timeslots" && tempStudent && (
									<Step2_3TimeSlots
										student={getCurrentStudent()!}
										onUpdate={updateStudent}
										onNext={goToNextStep}
										onPrevious={goToPreviousStep}
									/>
								)}

								{currentStep === "2.4-pap-pps" && tempStudent && (
									<Step2_4PapPps
										student={getCurrentStudent()!}
										onUpdate={updateStudent}
										onNext={goToNextStep}
										onPrevious={goToPreviousStep}
									/>
								)}

								{currentStep === "3-validation" && (
									<Step3ValidationPayment
										formData={formData}
										updateFormData={updateFormData}
										onSubmit={handleSubmit}
										onPrevious={goToPreviousStep}
										isPending={isPending || isCreatingSession}
									/>
								)}
							</div>
						</CardContent>
					</Card>

					<div className="mt-8 text-center">
						<p className="text-sm text-gray-600">
							Une question ? Contactez-nous par email à{" "}
							<a
								href="mailto:contact@revacours.com"
								className="text-[#F6993F] hover:underline font-medium"
							>
								contact@revacours.com
							</a>
						</p>
					</div>
				</div>
			</div>

			<InscriptionSteps />

			<ExperienceSection />

			<CoursSection />

			<PublicFooter />
		</div>
	);
}
